import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useDataCache } from '../context/DataCacheContext';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { ArrowLeft, Package, Edit, Download, Upload, RefreshCw, Plus, Minus, Truck, ShieldAlert, FileSpreadsheet, Layers, Box, Store, Save, ChevronDown, Replace, PlusCircle, Plane, Ship } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';

const SemaforoIcon = ({ color, daysToQuiebre, source }) => {
  const sourceLabel = source === 'full' ? 'FULL' : source === 'bodega' ? 'Bodega' : source === 'meli' ? 'MeLi' : 'Total';
  const tooltipText = daysToQuiebre < 999 
    ? `${daysToQuiebre} días (${sourceLabel})`
    : 'Sin ventas recientes';
  
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger>
        <div className={`w-3.5 h-3.5 rounded-full ${color === 'red' ? 'bg-red-500 animate-pulse' : color === 'yellow' ? 'bg-yellow-500' : 'bg-green-500'}`} />
      </TooltipTrigger>
      <TooltipContent side="right">
        <p className="text-xs">{tooltipText}</p>
      </TooltipContent>
    </Tooltip>
  );
};

const TipBtn = ({ onClick, icon: Icon, label, tip, color = 'bg-slate-700 hover:bg-slate-600', accept, onChange }) => {
  const btn = (
    <TooltipTrigger asChild>
      {accept ? (
        <label className={`${color} text-white h-8 px-3 rounded flex items-center gap-1.5 cursor-pointer text-xs font-medium`}>
          <Icon className="w-3.5 h-3.5" />{label}
          <input type="file" accept={accept} onChange={onChange} className="hidden" />
        </label>
      ) : (
        <button onClick={onClick} className={`${color} text-white h-8 px-3 rounded flex items-center gap-1.5 text-xs font-medium`}>
          <Icon className="w-3.5 h-3.5" />{label}
        </button>
      )}
    </TooltipTrigger>
  );
  return (
    <Tooltip delayDuration={200}>
      {btn}
      <TooltipContent side="bottom" className="max-w-[240px] text-xs">{tip}</TooltipContent>
    </Tooltip>
  );
};

const Inventory = () => {
  const { user, loading, authToken } = useAuth();
  const { fetchWithCache, invalidateCache } = useDataCache();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadProgress, setLoadProgress] = useState({ current: 0, total: 0 });
  const [editProduct, setEditProduct] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCogs, setEditCogs] = useState('');
  const [editSku, setEditSku] = useState('');
  const [editLeadTime, setEditLeadTime] = useState('18');
  const [editGoalDays, setEditGoalDays] = useState('60');
  const [adjustQty, setAdjustQty] = useState({});
  const [meliStockQty, setMeliStockQty] = useState({});
  const [savingMeliStock, setSavingMeliStock] = useState({});
  const [bulkMode, setBulkMode] = useState('set');
  const [filter, setFilter] = useState('all');
  const [cacheInfo, setCacheInfo] = useState(null);
  
  // Estado para dropdown de Stock Bodega
  const [bodegaDropdownOpen, setBodegaDropdownOpen] = useState(false);
  const bodegaDropdownRef = useRef(null);
  const bodegaFileInputRef = useRef(null);
  const [selectedBodegaMode, setSelectedBodegaMode] = useState(null);

  // Estado para dropdown de Gestión de Kits
  const [kitsDropdownOpen, setKitsDropdownOpen] = useState(false);
  const kitsDropdownRef = useRef(null);
  const kitsFileInputRef = useRef(null);

  // Estado para dropdown de Proyección de Compra (Restock)
  const [restockDropdownOpen, setRestockDropdownOpen] = useState(false);
  const restockDropdownRef = useRef(null);

  useEffect(() => { if (!loading && !user) navigate('/'); }, [user, loading, navigate]);
  
  // Cerrar dropdowns cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (bodegaDropdownRef.current && !bodegaDropdownRef.current.contains(event.target)) {
        setBodegaDropdownOpen(false);
      }
      if (kitsDropdownRef.current && !kitsDropdownRef.current.contains(event.target)) {
        setKitsDropdownOpen(false);
      }
      if (restockDropdownRef.current && !restockDropdownRef.current.contains(event.target)) {
        setRestockDropdownOpen(false);
      }
};
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchData = useCallback(async (forceRefresh = false) => {
    setLoadingData(true);
    setLoadingMore(false);
    setLoadProgress({ current: 0, total: 0 });
    
    try {
      // Carga inicial rápida - página 1
      const firstPageData = await fetchWithCache(
        'inventory_smart_page1',
        async () => {
          const r = await apiClient.get('/api/inventory/smart?page=1&limit=50');
          return r.data;
        },
        forceRefresh
      );
      
      // Capturar info del cache
      if (firstPageData.cache_info) {
        setCacheInfo(firstPageData.cache_info);
      }
      
      // Mostrar primeros productos inmediatamente
      const initialProducts = firstPageData.products || firstPageData.data || [];
      setProducts(initialProducts);
      setLoadingData(false);
      
      // Verificar si hay paginación y más páginas
      const pagination = firstPageData.pagination;
      if (pagination && pagination.total_pages > 1) {
        setLoadingMore(true);
        setLoadProgress({ current: initialProducts.length, total: pagination.total });
        
        // Cargar resto de páginas en background
        const totalPages = pagination.total_pages;
        let allProducts = [...initialProducts];
        
        for (let page = 2; page <= totalPages; page++) {
          try {
            const r = await apiClient.get(`/api/inventory/smart?page=${page}&limit=50`);
            const pageProducts = r.data.products || r.data.data || [];
            
            // Concatenar nuevos productos
            allProducts = [...allProducts, ...pageProducts];
            setProducts(allProducts);
            setLoadProgress({ current: allProducts.length, total: pagination.total });
          } catch (pageErr) {
            console.warn(`Error loading page ${page}:`, pageErr);
          }
        }
        
        setLoadingMore(false);
      }
    } catch (e) { 
      toast.error('Error al cargar inventario');
      setLoadingData(false);
    }
  }, [fetchWithCache]);

  // Cargar datos cuando hay usuario
  const inventoryLoadedRef = useRef(false);
  useEffect(() => {
    if (user && !inventoryLoadedRef.current) {
      inventoryLoadedRef.current = true;
      fetchData(false);
    }
  }, [user, fetchData]);

  // Función para importar kits desde el dropdown
  const handleKitsImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const fd = new FormData();
    fd.append('file', file);
    
    try {
      toast.info('Procesando archivo de Kits...');
      const r = await apiClient.post('/api/inventory/combos-import', fd, { 
        headers: { 'Content-Type': 'multipart/form-data' } 
      });
      toast.success(`${r.data.imported} recetas de kits importadas`);
      invalidateCache("inventory"); fetchData(true);
    } catch (err) {
      toast.error('Error al importar archivo de kits');
    }
    
    e.target.value = '';
    setKitsDropdownOpen(false);
  };

  const adjustBodega = async (itemId, variationId, delta) => {
    try { 
      const r = await apiClient.post('/api/inventory/bodega/' + itemId, { adjustment: delta, mode: 'adjust', variation_id: variationId || null }); 
      const newStock = r.data.stock_bodega;
      const sku = r.data.sku;
      const fullCount = r.data.full_updated || 0;
      const nonFullCount = r.data.non_full_updated || 0;
      const meliQueued = Boolean(r.data.meli_job_id); // MeLi push now runs async in sync-worker
      
      // Actualización LOCAL instantánea del estado (sin esperar fetchData)
      setProducts(prev => prev.map(p => {
        const isSameSku = sku && sku !== "Sin SKU en MeLi" && sku !== "" && p.sku === sku;
        const isSameItem = p.item_id === itemId && (!variationId || String(p.variation_id || "") === String(variationId));
        
        if (isSameSku || isSameItem) {
          const isFull = p.logistic_type === "fulfillment";
          const newBodegaMeli = isFull ? p.stock_bodega_meli : newStock;
          return {
            ...p,
            stock_bodega: newStock,
            stock_bodega_meli: newBodegaMeli,
            stock_total: (p.stock_full || 0) + newBodegaMeli + newStock + (p.stock_en_camino || 0)
          };
        }
        return p;
      }));
      
      if (meliQueued) {
        toast.success(`Stock: ${newStock} → sincronizando ${nonFullCount} pub. con MeLi...` + (fullCount > 0 ? ` + ${fullCount} FULL local` : ''));
      } else if (fullCount > 0 && nonFullCount === 0) {
        toast.success(`Stock bodega: ${newStock} (${fullCount} pub. FULL - solo local)`);
      } else {
        toast.success(`Stock: ${newStock} → ${r.data.publications_updated} pub. actualizada(s)`);
      }
      
      // Invalidar cache para próximas visitas, pero NO recargar ahora
      invalidateCache("inventory");
    }
    catch (e) { toast.error('Error al ajustar stock'); }
  };

  const setBodega = async (itemId, variationId) => {
    const rowKey = variationId ? `${itemId}:${variationId}` : itemId;
    const qty = parseInt(adjustQty[rowKey] || '0');
    try { 
      const r = await apiClient.post('/api/inventory/bodega/' + itemId, { adjustment: qty, mode: 'set', variation_id: variationId || null }); 
      setAdjustQty(prev => ({ ...prev, [rowKey]: '' }));
      
      const newStock = r.data.stock_bodega;
      const sku = r.data.sku;
      const fullCount = r.data.full_updated || 0;
      const nonFullCount = r.data.non_full_updated || 0;
      const meliQueued = Boolean(r.data.meli_job_id); // MeLi push now runs async in sync-worker
      
      // Actualización LOCAL instantánea del estado (sin esperar fetchData)
      setProducts(prev => prev.map(p => {
        const isSameSku = sku && sku !== "Sin SKU en MeLi" && sku !== "" && p.sku === sku;
        const isSameItem = p.item_id === itemId && (!variationId || String(p.variation_id || "") === String(variationId));
        
        if (isSameSku || isSameItem) {
          const isFull = p.logistic_type === "fulfillment";
          const newBodegaMeli = isFull ? p.stock_bodega_meli : newStock;
          return {
            ...p,
            stock_bodega: newStock,
            stock_bodega_meli: newBodegaMeli,
            stock_total: (p.stock_full || 0) + newBodegaMeli + newStock + (p.stock_en_camino || 0)
          };
        }
        return p;
      }));
      
      if (meliQueued) {
        toast.success(`Stock: ${newStock} → sincronizando ${nonFullCount} pub. con MeLi...` + (fullCount > 0 ? ` + ${fullCount} FULL local` : ''));
      } else if (fullCount > 0 && nonFullCount === 0) {
        toast.success(`Stock bodega: ${newStock} (${fullCount} pub. FULL - solo local)`);
      } else {
        toast.success(`Stock: ${newStock} → ${r.data.publications_updated} pub. actualizada(s)`);
      }
      
      // Invalidar cache para próximas visitas, pero NO recargar ahora
      invalidateCache("inventory");
    }
    catch (e) { toast.error('Error al establecer stock'); }
  };

  // Poll a MeLi stock-push job started by updateMeliStock — the actual PUT
  // to MeLi runs in sync-worker now, not in the backend.
  const pollMeliStockJob = async (jobId, key, newQty, attempt = 0) => {
    const POLL_INTERVAL = 1500;
    const MAX_ATTEMPTS = 20; // ~30s ceiling

    try {
      const { data } = await apiClient.get(`/api/inventory/meli-stock/status/${jobId}`);

      if (data.status === 'completed') {
        toast.success(`Stock actualizado en MeLi: ${newQty}`);
        setMeliStockQty(prev => ({ ...prev, [key]: '' }));
        invalidateCache("inventory"); fetchData(true);
        setSavingMeliStock(prev => ({ ...prev, [key]: false }));
        return;
      }
      if (data.status === 'error') {
        toast.error(data.error || 'Error al actualizar stock en MeLi');
        setSavingMeliStock(prev => ({ ...prev, [key]: false }));
        return;
      }
      if (attempt >= MAX_ATTEMPTS) {
        toast.error('La actualización está tardando más de lo esperado. Verifica en unos minutos.');
        setSavingMeliStock(prev => ({ ...prev, [key]: false }));
        return;
      }
      setTimeout(() => pollMeliStockJob(jobId, key, newQty, attempt + 1), POLL_INTERVAL);
    } catch (e) {
      toast.error('Error consultando el estado de la actualización');
      setSavingMeliStock(prev => ({ ...prev, [key]: false }));
    }
  };

  // Actualizar stock en Mercado Libre (solo para items no-Fulfillment)
  const updateMeliStock = async (product) => {
    const key = product.variation_id ? `${product.item_id}:${product.variation_id}` : product.item_id;
    const newQty = parseInt(meliStockQty[key] || '0');

    if (isNaN(newQty) || newQty < 0) {
      toast.error('Cantidad inválida');
      return;
    }

    setSavingMeliStock(prev => ({ ...prev, [key]: true }));

    try {
      const { data } = await apiClient.put('/api/inventory/meli-stock', {
        item_id: product.item_id,
        variation_id: product.variation_id || null,
        new_quantity: newQty
      });
      pollMeliStockJob(data.job_id, key, newQty);
    } catch (e) {
      const errorMsg = e.response?.data?.detail || 'Error al actualizar stock';
      toast.error(errorMsg);
      setSavingMeliStock(prev => ({ ...prev, [key]: false }));
    }
  };

  const openEdit = (p) => { setEditProduct(p); setEditCogs(String(p.cogs || 0)); setEditSku(p.sku || ''); setEditLeadTime(String(p.lead_time || 18)); setEditGoalDays(String(p.stock_goal_days || 60)); setDialogOpen(true); };

  const saveEdit = async () => {
    if (!editProduct) return;
    try {
      await apiClient.post('/api/products/' + editProduct.item_id, { cogs: parseFloat(editCogs) || 0, sku: editSku });
      await apiClient.put('/api/inventory/settings/' + editProduct.item_id, { lead_time: parseInt(editLeadTime) || 18, stock_goal_days: parseInt(editGoalDays) || 60 });
      toast.success('Actualizado'); setDialogOpen(false); invalidateCache("inventory"); fetchData(true);
    } catch (e) { toast.error('Error'); }
  };

  const dl = async (ep, fn) => {
    try {
      toast.info('Generando archivo...');
      const r = await apiClient.get('/api/inventory/' + ep, { responseType: 'blob' });
      
      // Verificar si la respuesta es un error JSON en lugar de un blob
      if (r.data.type === 'application/json') {
        const text = await r.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Error en el servidor');
      }
      
      const a = document.createElement('a'); 
      a.href = window.URL.createObjectURL(new Blob([r.data])); 
      a.download = fn;
      document.body.appendChild(a); 
      a.click(); 
      a.remove();
      toast.success('Archivo descargado');
    } catch (e) { 
      console.error('Error al generar archivo:', e);
      const errorMsg = e?.response?.data?.detail || e?.message || 'Error desconocido';
      toast.error(`Error al generar el archivo: ${errorMsg}. Verifica que los datos no estén vacíos.`);
    }
  };

  // Función para descargar proyección de compra con estrategia
  const downloadRestock = async (strategy) => {
    setRestockDropdownOpen(false);
    const filename = strategy === 'air' ? 'proyeccion_aerea.xlsx' : 'proyeccion_maritima.xlsx';
    toast.info(`Generando proyección ${strategy === 'air' ? 'aérea' : 'marítima'}...`);
    try {
      const r = await apiClient.get(`/api/inventory/export-restock?strategy=${strategy}`, { responseType: 'blob' });
      
      // Verificar si la respuesta es un error JSON
      if (r.data.type === 'application/json') {
        const text = await r.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Error en el servidor');
      }
      
      const a = document.createElement('a'); 
      a.href = window.URL.createObjectURL(new Blob([r.data])); 
      a.download = filename;
      document.body.appendChild(a); 
      a.click(); 
      a.remove();
      toast.success('Proyección descargada');
    } catch (e) { 
      console.error('Error al generar proyección:', e);
      const errorMsg = e?.response?.data?.detail || e?.message || 'Error desconocido';
      toast.error(`Error al generar proyección: ${errorMsg}`);
    }
  };

  const ul = async (e, ep, msg) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { const r = await apiClient.post('/api/inventory/' + ep, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success(msg(r.data)); invalidateCache("inventory"); fetchData(true); }
    catch (err) { toast.error('Error al cargar'); }
    e.target.value = '';
  };

  // Función para manejar la carga de stock bodega con el dropdown
  const handleBodegaUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedBodegaMode) return;
    
    const fd = new FormData();
    fd.append('file', file);
    
    const mode = selectedBodegaMode; // 'adjust' o 'set'
    const modeLabel = mode === 'adjust' ? 'Ajuste' : 'Sobrescritura';
    
    try {
      toast.info(`Procesando ${modeLabel}...`);
      const r = await apiClient.post(`/api/inventory/bodega-bulk?mode=${mode}`, fd, { 
        headers: { 'Content-Type': 'multipart/form-data' } 
      });
      
      const { updated, skus_updated, items_updated, not_found, publications_updated, meli_job_id, non_full_updated, mode_label } = r.data;

      let msg = `✅ ${updated} registros ${mode_label}`;

      // Detallar por tipo de actualización
      const details = [];
      if (skus_updated > 0) details.push(`${skus_updated} por SKU`);
      if (items_updated > 0) details.push(`${items_updated} por Publicación`);
      if (details.length > 0) msg += ` (${details.join(', ')})`;

      if (publications_updated > updated) {
        msg += ` → ${publications_updated} publicaciones totales`;
      }
      if (meli_job_id) {
        msg += ` | sincronizando ${non_full_updated} con MeLi...`;
      }
      if (not_found > 0) {
        msg += ` ⚠️ ${not_found} no encontrados`;
      }
      
      toast.success(msg);
      invalidateCache("inventory"); fetchData(true);
    } catch (err) {
      toast.error('Error al cargar archivo de stock');
    }
    
    e.target.value = '';
    setSelectedBodegaMode(null);
  };

  // Abrir selector de archivo para modo específico
  const selectBodegaMode = (mode) => {
    setSelectedBodegaMode(mode);
    setBodegaDropdownOpen(false);
    // Disparar click en el input de archivo
    setTimeout(() => {
      if (bodegaFileInputRef.current) {
        bodegaFileInputRef.current.click();
      }
    }, 100);
  };

  if (loading || loadingData) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
    </div>
  );

  const filtered = filter === 'all' ? products : products.filter(p => p.semaforo === filter);
  const reds = products.filter(p => p.semaforo === 'red').length;
  const yellows = products.filter(p => p.semaforo === 'yellow').length;
  const greens = products.filter(p => p.semaforo === 'green').length;

  return (
    <TooltipProvider>
    <div className="min-h-screen bg-slate-950">
      {/* Header compacto */}
      <div className="bg-slate-900/50 backdrop-blur-xl border-b border-slate-800 px-6 py-3">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="text-slate-400 hover:text-white"><ArrowLeft className="w-5 h-5" /></button>
            <h1 className="text-xl font-bold font-outfit text-white">Inventario</h1>
            {/* Total de productos - info rápida */}
            <span className="text-sm text-slate-400 ml-2">
              {products.length} productos
            </span>
            {/* Cache status indicator */}
            {cacheInfo?.from_cache && (
              <div className="text-xs text-slate-500 flex items-center gap-1 ml-2">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                Actualizado hace {cacheInfo.age_minutes || 0} min
              </div>
            )}
          </div>

          {/* Botones compactos con tooltips */}
          <div className="flex items-center gap-2">
            {/* Botón de descarga de plantilla Bodega */}
            <TipBtn onClick={() => dl('bodega-template', 'stock_bodega.xlsx')} icon={Download} label="Bodega" tip="Descargar plantilla de stock bodega física (SKU o Número de Publicación, Nombre, Cantidad)" />
            
            {/* NUEVO: Dropdown para Actualizar Stock Bodega */}
            <div className="relative" ref={bodegaDropdownRef}>
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <button 
                    onClick={() => setBodegaDropdownOpen(!bodegaDropdownOpen)}
                    className="bg-blue-600 hover:bg-blue-500 text-white h-8 px-3 rounded flex items-center gap-1.5 text-xs font-medium"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    Stock Bodega
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${bodegaDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[260px] text-xs">
                  Actualizar Stock de Bodega (Ajuste incremental o Sobrescritura masiva)
                </TooltipContent>
              </Tooltip>
              
              {/* Dropdown Menu - Stock Bodega */}
              {bodegaDropdownOpen && (
                <div 
                  className="absolute top-full left-0 mt-1 w-72 rounded-lg overflow-hidden"
                  style={{
                    zIndex: 9999,
                    backgroundColor: '#1e293b',
                    border: '1px solid #475569',
                    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.4), 0px 4px 12px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  <div 
                    className="p-3 border-b"
                    style={{ backgroundColor: '#0f172a', borderColor: '#475569' }}
                  >
                    <p className="text-sm font-semibold" style={{ color: '#f1f5f9' }}>Actualizar Stock Bodega</p>
                  </div>
                  
                  {/* Opción A: Ajuste Incremental */}
                  <button
                    onClick={() => selectBodegaMode('adjust')}
                    className="w-full px-4 py-3.5 flex items-start gap-3 text-left transition-all duration-150"
                    style={{ backgroundColor: '#1e293b' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#334155'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1e293b'}
                  >
                    <div className="p-2 rounded" style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)' }}>
                      <PlusCircle className="w-5 h-5" style={{ color: '#34d399' }} />
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#f8fafc', fontWeight: 600 }}>Agregar o Quitar (Ajuste)</p>
                      <p className="text-xs mt-1" style={{ color: '#94a3b8' }}>
                        Suma o resta al stock actual.<br/>
                        Ej: +5 suma, -2 resta
                      </p>
                    </div>
                  </button>
                  
                  {/* Separador */}
                  <div style={{ height: '1px', backgroundColor: '#475569' }} />
                  
                  {/* Opción B: Sobrescribir */}
                  <button
                    onClick={() => selectBodegaMode('set')}
                    className="w-full px-4 py-3.5 flex items-start gap-3 text-left transition-all duration-150"
                    style={{ backgroundColor: '#1e293b' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#334155'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1e293b'}
                  >
                    <div className="p-2 rounded" style={{ backgroundColor: 'rgba(245, 158, 11, 0.2)' }}>
                      <Replace className="w-5 h-5" style={{ color: '#fbbf24' }} />
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#f8fafc', fontWeight: 600 }}>Actualización Masiva (Sobrescribir)</p>
                      <p className="text-xs mt-1" style={{ color: '#94a3b8' }}>
                        Reemplaza el stock actual.<br/>
                        Ideal para inventarios generales
                      </p>
                    </div>
                  </button>
                </div>
              )}
              
              {/* Input oculto para selección de archivo */}
              <input 
                ref={bodegaFileInputRef}
                type="file" 
                accept=".xlsx" 
                onChange={handleBodegaUpload}
                className="hidden" 
              />
            </div>

            <div className="w-px h-6 bg-slate-700 mx-1.5" />

            {/* Dropdown de Gestión de Kits */}
            <div className="relative" ref={kitsDropdownRef}>
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <button 
                    onClick={() => setKitsDropdownOpen(!kitsDropdownOpen)}
                    className="bg-amber-600 hover:bg-amber-500 text-white h-8 px-3 rounded flex items-center gap-1.5 text-xs font-medium"
                  >
                    <Layers className="w-3.5 h-3.5" />
                    Kits
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${kitsDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[260px] text-xs">
                  Gestión de Kits y Combos (descargar plantilla o importar recetas)
                </TooltipContent>
              </Tooltip>
              
              {/* Dropdown Menu de Kits - Fondo sólido */}
              {kitsDropdownOpen && (
                <div 
                  className="absolute top-full left-0 mt-1 w-72 rounded-lg overflow-hidden"
                  style={{
                    zIndex: 9999,
                    backgroundColor: '#1e293b',
                    border: '1px solid #475569',
                    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.4), 0px 4px 12px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  <div 
                    className="p-3 border-b"
                    style={{ backgroundColor: '#0f172a', borderColor: '#475569' }}
                  >
                    <p className="text-sm font-semibold" style={{ color: '#f1f5f9' }}>Gestión de Kits</p>
                  </div>
                  
                  {/* Opción A: Descargar Plantilla */}
                  <button
                    onClick={() => {
                      dl('combos-template', 'plantilla_combos.xlsx');
                      setKitsDropdownOpen(false);
                    }}
                    className="w-full px-4 py-3.5 flex items-start gap-3 text-left transition-all duration-150"
                    style={{ backgroundColor: '#1e293b' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#334155'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1e293b'}
                  >
                    <div className="p-2 rounded" style={{ backgroundColor: 'rgba(100, 116, 139, 0.3)' }}>
                      <Download className="w-5 h-5" style={{ color: '#cbd5e1' }} />
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#f8fafc', fontWeight: 600 }}>Descargar Plantilla</p>
                      <p className="text-xs mt-1" style={{ color: '#94a3b8' }}>
                        Excel con formato para definir<br/>
                        recetas (SKU Padre vs Hijos)
                      </p>
                    </div>
                  </button>
                  
                  {/* Separador */}
                  <div style={{ height: '1px', backgroundColor: '#475569' }} />
                  
                  {/* Opción B: Importar Combos */}
                  <button
                    onClick={() => {
                      setKitsDropdownOpen(false);
                      setTimeout(() => {
                        if (kitsFileInputRef.current) {
                          kitsFileInputRef.current.click();
                        }
                      }, 100);
                    }}
                    className="w-full px-4 py-3.5 flex items-start gap-3 text-left transition-all duration-150"
                    style={{ backgroundColor: '#1e293b' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#334155'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1e293b'}
                  >
                    <div className="p-2 rounded" style={{ backgroundColor: 'rgba(245, 158, 11, 0.2)' }}>
                      <Upload className="w-5 h-5" style={{ color: '#fbbf24' }} />
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#f8fafc', fontWeight: 600 }}>Importar Combos</p>
                      <p className="text-xs mt-1" style={{ color: '#94a3b8' }}>
                        Cargar archivo Excel con las<br/>
                        relaciones Padre-Hijo
                      </p>
                    </div>
                  </button>
                </div>
              )}
              
              {/* Input oculto para selección de archivo de Kits */}
              <input 
                ref={kitsFileInputRef}
                type="file" 
                accept=".xlsx" 
                onChange={handleKitsImport}
                className="hidden" 
              />
            </div>

            <div className="w-px h-6 bg-slate-700 mx-1.5" />

            {/* Dropdown de Proyección de Compra - Bimodal */}
            <div className="relative" ref={restockDropdownRef}>
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <button 
                    onClick={() => setRestockDropdownOpen(!restockDropdownOpen)}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white h-8 px-3 rounded flex items-center gap-1.5 text-xs font-medium"
                  >
                    <ShieldAlert className="w-3.5 h-3.5" />
                    Restock
                    <ChevronDown className={`w-3 h-3 transition-transform ${restockDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Generar proyección de compra</TooltipContent>
              </Tooltip>
              
              {restockDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl py-1 z-50 min-w-[220px]">
                  <button
                    onClick={() => downloadRestock('air')}
                    className="w-full px-3 py-2 text-left hover:bg-slate-700 flex items-center gap-2 text-sm text-white"
                  >
                    <Plane className="w-4 h-4 text-orange-400" />
                    <div>
                      <div className="font-medium">Aérea (Rápida)</div>
                      <div className="text-xs text-slate-400">Stock ≤ 2 meses → Pedir 3 meses</div>
                    </div>
                  </button>
                  <button
                    onClick={() => downloadRestock('sea')}
                    className="w-full px-3 py-2 text-left hover:bg-slate-700 flex items-center gap-2 text-sm text-white"
                  >
                    <Ship className="w-4 h-4 text-cyan-400" />
                    <div>
                      <div className="font-medium">Marítima (Volumen)</div>
                      <div className="text-xs text-slate-400">Stock ≤ 3 meses → Pedir 4 meses</div>
                    </div>
                  </button>
                </div>
              )}
            </div>

            <TipBtn onClick={() => dl('export-send-full', 'Envio-de-stock-Full.xlsx')} icon={Truck} label="Full" color="bg-emerald-600 hover:bg-emerald-500" tip="Generar archivo OFICIAL de MercadoLibre para carga masiva en 'Gestión de Stock Full'. Incluye priorización por ventas." />
            <TipBtn onClick={() => dl('export', 'inventario_completo.xlsx')} icon={FileSpreadsheet} label="" tip="Exportar Datos: Descarga un Excel con el stock calculado, ventas reales y semaforos de alerta" />

          </div>
        </div>
      </div>

      <main className="container mx-auto px-4 py-4">
        {/* Indicador de carga progresiva */}
        {loadingMore && (
          <div className="mb-3 bg-blue-500/10 border border-blue-500/30 rounded-lg px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-blue-400 animate-spin" />
              <span className="text-sm text-blue-300">
                Cargando inventario completo... {loadProgress.current} de {loadProgress.total} productos
              </span>
            </div>
            <div className="w-32 bg-slate-800 rounded-full h-2 overflow-hidden">
              <div 
                className="h-full bg-blue-500 transition-all duration-300"
                style={{ width: `${loadProgress.total > 0 ? (loadProgress.current / loadProgress.total) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* SEMÁFORO DE INVENTARIO - KPI CARDS PRINCIPALES */}
        {/* ============================================ */}
        <div className="flex justify-center gap-6 mb-6" data-testid="inventory-kpi-section">
          {/* Tarjeta CRÍTICO (Rojo) */}
          <button
            data-testid="kpi-card-critical"
            onClick={() => setFilter(filter === 'red' ? 'all' : 'red')}
            className={`
              flex-1 max-w-xs p-6 rounded-xl cursor-pointer transition-all duration-200
              bg-slate-900/80 border-l-4 border-red-500
              ${filter === 'red' 
                ? 'ring-2 ring-red-500 shadow-lg shadow-red-500/20 scale-[1.02]' 
                : 'hover:shadow-lg hover:shadow-red-500/10 hover:scale-[1.01]'}
            `}
            style={{ 
              boxShadow: filter === 'red' 
                ? '0 10px 40px rgba(239, 68, 68, 0.15)' 
                : '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-4 h-4 rounded-full bg-red-500 mb-3 animate-pulse" />
              <span className="text-slate-400 text-sm font-medium uppercase tracking-wide mb-2">
                Agotados / Críticos
              </span>
              <span className="text-5xl font-bold text-red-500 mb-1">
                {reds}
              </span>
              <span className="text-xs text-slate-500">
                Stock = 0 o &lt; 5 días
              </span>
            </div>
          </button>

          {/* Tarjeta ADVERTENCIA (Amarillo/Naranja) */}
          <button
            data-testid="kpi-card-warning"
            onClick={() => setFilter(filter === 'yellow' ? 'all' : 'yellow')}
            className={`
              flex-1 max-w-xs p-6 rounded-xl cursor-pointer transition-all duration-200
              bg-slate-900/80 border-l-4 border-amber-500
              ${filter === 'yellow' 
                ? 'ring-2 ring-amber-500 shadow-lg shadow-amber-500/20 scale-[1.02]' 
                : 'hover:shadow-lg hover:shadow-amber-500/10 hover:scale-[1.01]'}
            `}
            style={{ 
              boxShadow: filter === 'yellow' 
                ? '0 10px 40px rgba(245, 158, 11, 0.15)' 
                : '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-4 h-4 rounded-full bg-amber-500 mb-3" />
              <span className="text-slate-400 text-sm font-medium uppercase tracking-wide mb-2">
                Por Reabastecer
              </span>
              <span className="text-5xl font-bold text-amber-500 mb-1">
                {yellows}
              </span>
              <span className="text-xs text-slate-500">
                Entre 5 y 30 días
              </span>
            </div>
          </button>

          {/* Tarjeta SALUDABLE (Verde) */}
          <button
            data-testid="kpi-card-healthy"
            onClick={() => setFilter(filter === 'green' ? 'all' : 'green')}
            className={`
              flex-1 max-w-xs p-6 rounded-xl cursor-pointer transition-all duration-200
              bg-slate-900/80 border-l-4 border-emerald-500
              ${filter === 'green' 
                ? 'ring-2 ring-emerald-500 shadow-lg shadow-emerald-500/20 scale-[1.02]' 
                : 'hover:shadow-lg hover:shadow-emerald-500/10 hover:scale-[1.01]'}
            `}
            style={{ 
              boxShadow: filter === 'green' 
                ? '0 10px 40px rgba(16, 185, 129, 0.15)' 
                : '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-4 h-4 rounded-full bg-emerald-500 mb-3" />
              <span className="text-slate-400 text-sm font-medium uppercase tracking-wide mb-2">
                Stock Óptimo
              </span>
              <span className="text-5xl font-bold text-emerald-500 mb-1">
                {greens}
              </span>
              <span className="text-xs text-slate-500">
                Más de 30 días
              </span>
            </div>
          </button>
        </div>

        {/* Indicador de filtro activo */}
        {filter !== 'all' && (
          <div className="flex justify-center mb-4" data-testid="filter-indicator">
            <div className={`
              inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium
              ${filter === 'red' ? 'bg-red-500/20 text-red-400' : ''}
              ${filter === 'yellow' ? 'bg-amber-500/20 text-amber-400' : ''}
              ${filter === 'green' ? 'bg-emerald-500/20 text-emerald-400' : ''}
            `}>
              <span data-testid="filter-label">
                Mostrando: {filter === 'red' ? 'Críticos' : filter === 'yellow' ? 'Por Reabastecer' : 'Stock Óptimo'}
              </span>
              <span className="text-slate-400">|</span>
              <span data-testid="filtered-count">{filtered.length} productos</span>
              <button 
                data-testid="clear-filter-btn"
                onClick={() => setFilter('all')}
                className="ml-2 text-slate-400 hover:text-white transition-colors"
              >
                ✕ Limpiar filtro
              </button>
            </div>
          </div>
        )}
        
        {/* Tabla maximizada */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto max-h-[calc(100vh-100px)] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-800/70 sticky top-0 z-10">
                <tr className="text-slate-200 text-sm font-semibold">
                  <th className="px-2 py-3 text-left w-6"></th>
                  <th className="px-3 py-3 text-left">Producto</th>
                  {/* Columna "En Camino" ocultada visualmente - datos preservados para cálculo de Restock */}
                  <th className="px-2 py-3 text-center">
                    <Tooltip delayDuration={200}>
                      <TooltipTrigger className="cursor-help">Stock FULL</TooltipTrigger>
                      <TooltipContent>Stock real vendible en Fulfillment de MeLi</TooltipContent>
                    </Tooltip>
                  </th>
                  <th className="px-2 py-3 text-center">
                    <Tooltip delayDuration={200}>
                      <TooltipTrigger className="cursor-help flex items-center gap-1 justify-center">
                        <Store className="w-3.5 h-3.5" />Stock MeLi
                      </TooltipTrigger>
                      <TooltipContent>Stock en tu bodega publicado en MeLi (NO Fulfillment). Editable.</TooltipContent>
                    </Tooltip>
                  </th>
                  <th className="px-2 py-3 text-center">Stock Bodega</th>
                  <th className="px-2 py-3 text-center">Total</th>
                  <th className="px-2 py-3 text-center">Vtas 30D</th>
                  <th className="px-2 py-3 text-center">Dias Stock</th>
                  <th className="px-2 py-3 text-center">Quiebre</th>
                  <th className="px-2 py-3 text-center">Alertas</th>
                  <th className="px-2 py-3 text-center">+/- Bodega</th>
                  <th className="px-2 py-3 w-6"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {filtered.map(p => {
                  const rowKey = p.variation_id ? `${p.item_id}:${p.variation_id}` : p.item_id;
                  const isFulfillment = p.is_fulfillment || p.logistic_type === 'fulfillment';
                  
                  return (
                  <tr key={p.item_id + p.sku} className="hover:bg-slate-800/30 transition-colors">
                    <td className="px-2 py-2.5"><SemaforoIcon color={p.semaforo} daysToQuiebre={p.days_to_death || 999} source={p.stock_source} /></td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        {p.thumbnail && <img src={p.thumbnail} alt="" className="w-8 h-8 rounded" />}
                        <div>
                          <p className="text-slate-100 text-[13px] font-medium truncate max-w-[180px]">{p.title}</p>
                          <p className="text-[11px] text-slate-400 font-mono">
                            {p.sku || '-'} <span className="text-blue-400/80">{p.item_id}</span>
                            {isFulfillment && <span className="ml-1 px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded text-[9px] font-medium">FULL</span>}
                          </p>
                        </div>
                      </div>
                    </td>
                    {/* Columna "En Camino" ocultada - dato p.stock_en_camino preservado para algoritmo Restock */}
                    <td className="px-2 py-2.5 text-center">
                      <div className="flex flex-col items-center">
                        <span className="text-blue-400 text-sm font-semibold">{p.stock_full}</span>
                        {p.stock_full_not_available > 0 && (
                          <span className="text-[9px] text-red-400">({p.stock_full_not_available} no disp.)</span>
                        )}
                      </div>
                    </td>
                    {/* Nueva columna: Stock Bodega MeLi (editable para no-fulfillment) */}
                    <td className="px-2 py-2.5 text-center">
                      {isFulfillment ? (
                        <span className="text-slate-600 text-sm">-</span>
                      ) : (
                        <div className="flex items-center justify-center gap-1">
                          <input
                            type="number"
                            value={meliStockQty[rowKey] ?? ''}
                            onChange={e => setMeliStockQty(prev => ({ ...prev, [rowKey]: e.target.value }))}
                            onKeyDown={e => e.key === 'Enter' && updateMeliStock(p)}
                            placeholder={String(p.stock_bodega_meli || 0)}
                            className="w-12 h-6 bg-slate-800 border border-cyan-500/30 rounded text-center text-xs text-cyan-300 font-medium"
                          />
                          <button
                            onClick={() => updateMeliStock(p)}
                            disabled={savingMeliStock[rowKey]}
                            className="w-6 h-6 bg-cyan-500/20 text-cyan-400 rounded flex items-center justify-center hover:bg-cyan-500/30 disabled:opacity-50"
                          >
                            {savingMeliStock[rowKey] ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              <Save className="w-3 h-3" />
                            )}
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2.5 text-center text-amber-400 text-sm font-semibold">{p.stock_bodega}</td>
                    <td className="px-2 py-2.5 text-center text-slate-100 font-bold text-sm">{p.stock_total}</td>
                    <td className="px-2 py-2.5 text-center text-slate-200 text-sm">{p.ventas_30 || 0}</td>
                    <td className="px-2 py-2.5 text-center">
                      <Tooltip delayDuration={200}>
                        <TooltipTrigger>
                          <span className={`text-sm font-bold ${(p.days_stock || 0) === 0 ? 'text-red-400' : (p.days_stock || 999) <= 10 ? 'text-red-400' : (p.days_stock || 999) <= 30 ? 'text-yellow-400' : 'text-green-400'}`}>
                            {(p.days_stock === null || p.days_stock === undefined || p.days_stock >= 999) ? '-' : p.days_stock + 'd'}
                            {p.stock_source === 'full' && <span className="ml-0.5 text-blue-400 text-[9px]">F</span>}
                            {p.stock_source === 'bodega' && <span className="ml-0.5 text-amber-400 text-[9px]">B</span>}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {p.stock_source === 'full' && 'Calculado desde Stock FULL'}
                          {p.stock_source === 'bodega' && 'Calculado desde Stock Bodega'}
                          {p.stock_source === 'meli' && 'Calculado desde Stock MeLi'}
                          {p.stock_source === 'total' && 'Calculado desde Stock Total'}
                          {p.stock_source === 'zero' && 'Sin stock disponible'}
                          {!p.stock_source && 'Sin datos de ventas'}
                        </TooltipContent>
                      </Tooltip>
                    </td>
                    <td className="px-2 py-2.5 text-center">
                      {p.death_date ? (
                        <Tooltip delayDuration={200}>
                          <TooltipTrigger>
                            <span className={`text-xs font-medium ${p.days_stock_total <= 15 ? 'text-red-400' : 'text-slate-300'}`}>
                              {p.death_date}
                              {p.death_stock_source === 'full' && <span className="ml-0.5 text-blue-400">●</span>}
                              {p.death_stock_source === 'bodega' && <span className="ml-0.5 text-amber-400">●</span>}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {p.death_stock_source === 'full' && 'Calculado desde Stock FULL'}
                            {p.death_stock_source === 'bodega' && 'Calculado desde Stock Bodega'}
                            {p.death_stock_source === 'meli' && 'Calculado desde Stock MeLi'}
                            {p.death_stock_source === 'total' && 'Calculado desde Stock Total'}
                          </TooltipContent>
                        </Tooltip>
                      ) : <span className="text-slate-600">-</span>}
                    </td>
                    <td className="px-2 py-2.5 text-center">
                      <div className="flex flex-col gap-1">
                        {p.alert_restock && <span className="text-[10px] px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded-full font-medium">Comprar {p.suggested_buy}</span>}
                        {p.alert_send_full && <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded-full font-medium">FULL +{p.suggested_send_full}</span>}
                      </div>
                    </td>
                    <td className="px-2 py-2.5">
                      <div className="flex items-center gap-1">
                        <button data-testid={`bodega-minus-${p.item_id}-${p.variation_id}`} onClick={() => adjustBodega(p.item_id, p.variation_id, -1)} className="w-6 h-6 bg-red-500/20 text-red-400 rounded flex items-center justify-center hover:bg-red-500/30"><Minus className="w-3 h-3" /></button>
                        <input data-testid={`bodega-input-${p.item_id}-${p.variation_id}`} type="number" value={adjustQty[p.variation_id ? `${p.item_id}:${p.variation_id}` : p.item_id] || ''} onChange={e => setAdjustQty(prev => ({ ...prev, [p.variation_id ? `${p.item_id}:${p.variation_id}` : p.item_id]: e.target.value }))}
                          onKeyDown={e => e.key === 'Enter' && setBodega(p.item_id, p.variation_id)}
                          placeholder={String(p.stock_bodega)} className="w-11 h-6 bg-slate-800 border border-slate-700 rounded text-center text-xs text-slate-100 font-medium" />
                        <button data-testid={`bodega-plus-${p.item_id}-${p.variation_id}`} onClick={() => adjustBodega(p.item_id, p.variation_id, 1)} className="w-6 h-6 bg-green-500/20 text-green-400 rounded flex items-center justify-center hover:bg-green-500/30"><Plus className="w-3 h-3" /></button>
                      </div>
                    </td>
                    <td className="px-2 py-2.5">
                      <button onClick={() => openEdit(p)} className="text-slate-400 hover:text-blue-400"><Edit className="w-4 h-4" /></button>
                    </td>
                  </tr>
                )})}
              </tbody>
            </table>
          </div>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-slate-900 border-slate-800 text-white">
            <DialogHeader><DialogTitle className="font-outfit">Configurar Producto</DialogTitle></DialogHeader>
            {editProduct && (
              <div className="space-y-4 mt-4">
                <p className="text-sm text-slate-300">{editProduct.title}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-slate-400 mb-1 block">SKU</label><Input value={editSku} onChange={e => setEditSku(e.target.value)} className="bg-slate-950/50 border-slate-700 text-white" /></div>
                  <div><label className="text-xs text-slate-400 mb-1 block">COGS</label><Input type="number" value={editCogs} onChange={e => setEditCogs(e.target.value)} className="bg-slate-950/50 border-slate-700 text-white" /></div>
                  <div><label className="text-xs text-slate-400 mb-1 block">Lead Time</label>
                    <select value={editLeadTime} onChange={e => setEditLeadTime(e.target.value)} className="w-full bg-slate-950/50 border border-slate-700 rounded-md text-white text-sm px-3 h-10">
                      <option value="18">Aereo/Local (18d)</option><option value="90">Barco (90d)</option><option value="5">Rapido (5d)</option>
                    </select></div>
                  <div><label className="text-xs text-slate-400 mb-1 block">Meta stock</label>
                    <select value={editGoalDays} onChange={e => setEditGoalDays(e.target.value)} className="w-full bg-slate-950/50 border border-slate-700 rounded-md text-white text-sm px-3 h-10">
                      <option value="60">2 meses (60d)</option><option value="90">3 meses (90d)</option><option value="180">6 meses (180d)</option>
                    </select></div>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button onClick={saveEdit} className="flex-1 bg-blue-600 hover:bg-blue-500">Guardar</Button>
                  <Button onClick={() => setDialogOpen(false)} variant="outline" className="flex-1 border-slate-700">Cancelar</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
    </TooltipProvider>
  );
};

export default Inventory;
